interface GameSession {
  id: string;
  name: string;
  numPlayers: 2 | 3 | 4;
  status: 'DRAFT' | 'SETUP' | 'ACTIVE' | 'COMPLETED';
  createdAt: string;
  startedAt?: string;
  endedAt?: string;
}

export interface SessionHeaderProps {
  session: GameSession | null;
  mode: 'setup' | 'live';
  onNavigate?: () => void;
  onBack?: () => void;
}

const STATUS_STYLES = {
  DRAFT: { bg: 'bg-gray-700', text: 'text-gray-300', label: 'Draft' },
  SETUP: { bg: 'bg-blue-700', text: 'text-blue-200', label: 'Setup' },
  ACTIVE: { bg: 'bg-green-700', text: 'text-green-200', label: 'Active' },
  COMPLETED: { bg: 'bg-purple-700', text: 'text-purple-200', label: 'Completed' },
};

export function SessionHeader({ session, mode, onNavigate, onBack }: SessionHeaderProps) {
  if (!session) {
    return (
      <div className="mb-6">
        <div className="animate-pulse h-12 bg-gray-800 rounded"></div>
      </div>
    );
  }

  const statusStyle = STATUS_STYLES[session.status];

  return (
    <div className="mb-6">
      {/* Back Link */}
      {onBack && (
        <button
          onClick={onBack}
          className="text-gray-400 hover:text-white flex items-center gap-2 text-sm font-medium transition-colors mb-4"
        >
          ← Back to Dashboard
        </button>
      )}

      <div className="flex items-center justify-between">
        {/* Left: Session Info */}
        <div className="flex items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">{session.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-gray-400 text-sm">
                {session.numPlayers} Players
              </span>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
                {statusStyle.label}
              </span>
              {mode === 'live' && (
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-red-700 text-red-200">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                  LIVE
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right: Navigation Button */}
        {onNavigate && mode === 'setup' && (
          <button
            onClick={onNavigate}
            className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-xl font-bold transition-colors shadow-lg"
          >
            🚀 Go Live
          </button>
        )}

        {onNavigate && mode === 'live' && (
          <button
            onClick={onNavigate}
            className="text-purple-400 hover:text-purple-300 flex items-center gap-2 text-sm font-semibold transition-colors"
          >
            ← Back to Setup
          </button>
        )}
      </div>
    </div>
  );
}
